
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import defensiveCopy from "@/util/defensive-copy";
import { VuetifyShapeForSelect } from "@/types/conditions";
import { VAutocomplete, VSelect, VCombobox } from "vuetify/lib";
import { fetchOptionData } from "@/lib/wordpress/data";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { ControlsTreeContext, MultiselectOptions } from "@/types/controls";
import { OnPropertyChange } from "@/types/store-actions";

@Component({
  components: { Icon, VSelect, VAutocomplete, VCombobox }
})
export default class MultiselectInput extends Vue {
  @Prop()
  value!: unknown[];

  @Prop()
  placeholder!: string;

  @Prop()
  search!: string;

  @Prop()
  loading!: boolean;

  @Prop()
  items?: (VuetifyShapeForSelect | string)[];

  @Prop({ default: false })
  clearable!: boolean;

  @Prop({ default: false })
  searchable!: boolean;

  @Prop({ default: false })
  emitObject!: boolean;

  @Prop()
  options?: MultiselectOptions;

  get componentType() {
    if (this.searchable) return "v-autocomplete";

    if (this.options?.makeCombobox) return "v-combobox";

    return "v-select";
  }

  get internalItems() {
    if (this.options?.populate?.fetchDataAction && this.retrievedItems.length) {
      return this.retrievedItems;
    }

    return this.items;
  }

  get internalValue() {
    return this.value || [];
  }

  edit(selectedItems: unknown) {
    this.$emit("input", selectedItems);
  }

  get chips() {
    const items = this.internalItems?.filter(
      item => typeof item !== "string" && "text" in item
    ) as
      | {
          text: string;
          value: string;
        }[]
      | null;

    return this.internalValue.map(v => {
      const found = items?.find(i => i.value === v);
      return found || v;
    });
  }

  deleteItem(indexToRemove: number) {
    const newItems = defensiveCopy(this.internalValue) || [];
    newItems.splice(indexToRemove, 1);
    this.$emit("input", newItems);
  }

  @Inject({ from: "context", default: null })
  readonly context!: ControlsTreeContext | null;

  created() {
    if (
      this.options?.populate?.fetchDataAction &&
      this.retrievedItems.length === 0
    ) {
      this.retrieveItems();
    }

    breakdanceStoreActions.register.onPropertyChange(this.retrieveItemsAction);
  }

  beforeDestroy() {
    breakdanceStoreActions.unregister(
      "onPropertyChange",
      this.retrieveItemsAction
    );
  }

  retrieveItemsAction({ path }: OnPropertyChange) {
    if (this.fetchingOptionData || !this.context) {
      return;
    }

    const refetchPaths = this.options?.populate?.refetchPaths;

    if (refetchPaths && refetchPaths.includes(path)) {
      this.retrieveItems();
    }
  }

  fetchingOptionData = false;
  retrievedItems: { text: string; value: string }[] = [];
  async retrieveItems() {
    const action = this.options?.populate?.fetchDataAction;

    if (!action) {
      return;
    }

    let contextData: unknown = {};

    if (this.context && this.options?.populate?.fetchContextPath) {
      contextData = this.context.getControlValue(
        this.options?.populate.fetchContextPath
      );
    }

    this.fetchingOptionData = true;

    this.retrievedItems = await fetchOptionData(action, {
      context: contextData
    });

    this.fetchingOptionData = false;
  }
}
